/**
 * FragmentEditorModal Component
 *
 * Quick editor modal for creating/updating fragments from Fragment Discovery Mode.
 * Shows when clicking on fragment markers in the UI.
 *
 * @since 2026-01-09
 * @updated 2026-03-11 - Roles como multi-select combobox
 * @updated 2026-05-08 - [TMF9-54249] Lookup por code (nao mais list+find paginado);
 *   botao Salvar nao depende mais de !fragmentsResponse; fallback para
 *   "already exists" no create -> refetch + update.
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useFragmentMode } from '@/contexts/FragmentModeContext';
import { useAvailableProfiles } from '@/hooks/useAccessTypes';
import {
  getFragmentByCode,
  createFragment,
  updateFragment,
  ContentFragment,
  ContentFragmentCreate,
  ContentFragmentUpdate,
} from '@/services/fragmentApi';

export function FragmentEditorModal() {
  const { t } = useTranslation('modules/fragment');
  const queryClient = useQueryClient();
  const { selectedFragmentCode, closeFragmentEditor } = useFragmentMode();

  const [formData, setFormData] = useState({
    code: '',
    selectedRoles: [] as string[],
    status: 1,
  });
  const [existingFragment, setExistingFragment] = useState<ContentFragment | null>(null);
  const [roleSearch, setRoleSearch] = useState('');

  // Load available profiles (same source as Visibility tab)
  const { data: profilesData } = useAvailableProfiles();

  // [TMF9-54249] Lookup direto por code (em vez de listar 1000 e fazer find).
  // queryFn devolve null em caso de 404 -> indica modo CREATE.
  // Qualquer outro erro propaga e marcamos como nao-resolvido (tratado abaixo).
  const {
    data: lookupResult,
    isLoading: lookupLoading,
    isError: lookupError,
  } = useQuery({
    queryKey: ['fragment', 'by-code', selectedFragmentCode],
    queryFn: async (): Promise<ContentFragment | null> => {
      if (!selectedFragmentCode) return null;
      try {
        return await getFragmentByCode(selectedFragmentCode);
      } catch (err: any) {
        if (err?.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!selectedFragmentCode,
    refetchOnMount: 'always',
    staleTime: 0,
    retry: false,
  });

  // Parse role string to array
  const parseRoles = (roleStr: string | null): string[] => {
    if (!roleStr) return [];
    try {
      const parsed = JSON.parse(roleStr);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // Not JSON, try CSV
    }
    return roleStr.split(',').map((r) => r.trim()).filter(Boolean);
  };

  // [TMF9-54249] Bootstrap imediato: assim que o modal abre, garantimos que
  // formData.code recebe o selectedFragmentCode (sem esperar a query terminar).
  // Isso evita o bug em que o user clica Salvar antes do lookup terminar e o
  // POST sai com code='', criando um fragment orfao que depois bloqueia todos
  // os saves seguintes com "Fragment with code '' already exists".
  useEffect(() => {
    if (selectedFragmentCode) {
      setFormData((prev) => ({
        ...prev,
        code: selectedFragmentCode,
      }));
    }
  }, [selectedFragmentCode]);

  // Hidrata o form quando o lookup termina (sucesso = update; null = create).
  // Quando lookupError ocorre, mantemos os defaults e deixamos o user salvar
  // mesmo assim — o servidor decide e o fallback no onError do create cuida
  // do caso "ja existe".
  useEffect(() => {
    if (!selectedFragmentCode) return;
    if (lookupLoading) return;

    if (lookupResult) {
      setExistingFragment(lookupResult);
      setFormData({
        code: lookupResult.code,
        selectedRoles: parseRoles(lookupResult.role),
        status: lookupResult.status,
      });
    } else {
      // 404 ou erro: assume CREATE com defaults
      setExistingFragment(null);
      setFormData((prev) => ({
        code: selectedFragmentCode,
        selectedRoles: prev.code === selectedFragmentCode ? prev.selectedRoles : [],
        status: prev.code === selectedFragmentCode ? prev.status : 1,
      }));
    }
  }, [selectedFragmentCode, lookupResult, lookupLoading]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: ContentFragmentUpdate }) =>
      updateFragment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fragments'] });
      queryClient.invalidateQueries({ queryKey: ['fragment'] });
      toast.success(t('messages.success.updated'));
      handleClose();
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || t('messages.error.update');
      toast.error(message);
    },
  });

  // Create mutation com fallback automatico para update quando o backend
  // sinaliza "already exists" (race entre lookup e create de outros tabs).
  const createMutation = useMutation({
    mutationFn: createFragment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fragments'] });
      queryClient.invalidateQueries({ queryKey: ['fragment'] });
      toast.success(t('messages.success.created'));
      handleClose();
    },
    onError: async (error: any, variables) => {
      const detail: string = error?.response?.data?.detail || '';
      const status = error?.response?.status;
      const isDuplicate = status === 400 && /already exists/i.test(detail);

      if (isDuplicate && variables?.code) {
        // [TMF9-54249] Fragment ja existe (orfao do bug original ou criado por
        // outra aba). Buscamos pelo code e fazemos update transparente.
        try {
          const found = await getFragmentByCode(variables.code);
          await updateMutation.mutateAsync({
            id: found.id,
            data: {
              code: variables.code,
              role: variables.role ?? null,
              status: variables.status ?? 1,
            },
          });
          return;
        } catch (recoverErr: any) {
          const recoverMsg =
            recoverErr?.response?.data?.detail || t('messages.error.update');
          toast.error(recoverMsg);
          return;
        }
      }

      const message = detail || t('messages.error.create');
      toast.error(message);
    },
  });

  const toggleRole = (roleName: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedRoles: prev.selectedRoles.includes(roleName)
        ? prev.selectedRoles.filter((r) => r !== roleName)
        : [...prev.selectedRoles, roleName],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // [TMF9-54249] Fonte de verdade do code = selectedFragmentCode (vindo do
    // FragmentGate). formData.code pode estar vazio se o user submeteu antes
    // do useEffect rodar.
    const codeToSubmit = (selectedFragmentCode || formData.code).trim();
    if (!codeToSubmit) {
      toast.error(
        t('messages.error.codeRequired', { defaultValue: 'Codigo do fragmento ausente' }),
      );
      return;
    }

    const roleValue =
      formData.selectedRoles.length > 0 ? JSON.stringify(formData.selectedRoles) : null;

    if (existingFragment) {
      updateMutation.mutate({
        id: existingFragment.id,
        data: {
          code: codeToSubmit,
          role: roleValue,
          status: formData.status,
        },
      });
    } else {
      const createData: ContentFragmentCreate = {
        code: codeToSubmit,
        role: roleValue,
        status: formData.status,
      };
      createMutation.mutate(createData);
    }
  };

  const handleClose = () => {
    setFormData({ code: '', selectedRoles: [], status: 1 });
    setExistingFragment(null);
    setRoleSearch('');
    closeFragmentEditor();
  };

  if (!selectedFragmentCode) return null;

  const availableRoles = (profilesData || []).map((p) => ({
    name: p.name,
    description: p.title || p.name,
  }));
  const filteredRoles = roleSearch
    ? availableRoles.filter(
        (r) =>
          r.name.toLowerCase().includes(roleSearch.toLowerCase()) ||
          r.description.toLowerCase().includes(roleSearch.toLowerCase()),
      )
    : availableRoles;

  // [TMF9-54249] Botao Salvar fica habilitado desde que ha codigo e nao ha
  // mutation em andamento. NAO depende mais de !fragmentsResponse — se o
  // lookup falhar ou demorar, o user ainda consegue salvar; o servidor +
  // fallback de "already exists" no onError garantem o comportamento certo.
  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const submitDisabled = isSubmitting || !selectedFragmentCode;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        <span className="hidden sm:inline-block sm:h-screen sm:align-middle">&#8203;</span>

        {/* Modal */}
        <div className="inline-block transform overflow-hidden rounded-lg bg-popover text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="bg-purple-600 px-6 py-4 rounded-t-lg">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">
                  {existingFragment ? (
                    <>
                      <i className="bi bi-pencil mr-2" />
                      Editar Fragmento
                    </>
                  ) : (
                    <>
                      <i className="bi bi-plus-circle mr-2" />
                      Criar Fragmento
                    </>
                  )}
                </h3>
                <button
                  type="button"
                  onClick={handleClose}
                  className="text-white/80 hover:text-white"
                >
                  <i className="bi bi-x-lg text-xl" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-4">
              {/* Code Field */}
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-foreground">
                  Codigo <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="mt-1 block w-full rounded-lg border-border shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm font-mono"
                  placeholder="edit_content_button"
                  disabled={!!existingFragment}
                />
                {lookupError && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    Nao foi possivel verificar se o fragmento ja existe. Voce pode tentar
                    salvar mesmo assim — se ja existir sera atualizado automaticamente.
                  </p>
                )}
              </div>

              {/* Roles Checkbox List */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Perfis com acesso
                </label>

                <div className="rounded-lg border border-border bg-popover">
                  {/* Toolbar: Selecionar todos | Limpar seleção */}
                  <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (availableRoles.length > 0) {
                            setFormData((prev) => ({
                              ...prev,
                              selectedRoles: availableRoles.map((r) => r.name),
                            }));
                          }
                        }}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200"
                      >
                        Selecionar todos
                      </button>
                      <span className="text-muted-foreground">|</span>
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, selectedRoles: [] }))}
                        className="text-xs font-medium text-muted-foreground hover:text-foreground"
                      >
                        Limpar seleção
                      </button>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formData.selectedRoles.length} selecionado(s)
                    </span>
                  </div>

                  {/* Search filter */}
                  <div className="px-3 py-2 border-b border-border">
                    <div className="relative">
                      <input
                        type="text"
                        value={roleSearch}
                        onChange={(e) => setRoleSearch(e.target.value)}
                        className="block w-full rounded-lg border-border shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm py-1.5 pl-8"
                        placeholder="Buscar perfil..."
                      />
                      <i className="bi bi-search absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs" />
                    </div>
                  </div>

                  {/* Checkbox list */}
                  <ul className="max-h-64 overflow-y-auto">
                    {filteredRoles.length === 0 ? (
                      <li className="px-3 py-3 text-sm text-muted-foreground text-center">
                        {availableRoles.length === 0 ? 'Carregando perfis...' : 'Nenhum perfil encontrado'}
                      </li>
                    ) : (
                      filteredRoles.map((role) => {
                        const isSelected = formData.selectedRoles.includes(role.name);
                        return (
                          <li
                            key={role.name}
                            onClick={() => toggleRole(role.name)}
                            className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                              isSelected
                                ? 'bg-blue-50 dark:bg-blue-900/30'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-600/50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleRole(role.name)}
                              onClick={(e) => e.stopPropagation()}
                              className="h-4 w-4 rounded border-border text-blue-600 focus:ring-blue-500"
                            />
                            <i className="bi bi-person-badge text-blue-500 dark:text-blue-300 text-sm" />
                            <div className="flex-1 min-w-0">
                              <span className={`text-sm font-medium ${isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-foreground'}`}>
                                {role.description || role.name}
                              </span>
                              {role.description && role.description !== role.name && (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  {role.name}
                                </span>
                              )}
                            </div>
                          </li>
                        );
                      })
                    )}
                  </ul>
                </div>

                <p className="mt-1 text-xs text-muted-foreground">
                  {formData.selectedRoles.length === 0
                    ? 'Nenhum perfil selecionado = visível para todos'
                    : `${formData.selectedRoles.length} perfil(is) selecionado(s)`}
                </p>
              </div>

              {/* Status Field */}
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-foreground">
                  Status
                </label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: parseInt(e.target.value) })
                  }
                  className="mt-1 block w-full rounded-lg border-border shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                >
                  <option value={1}>Ativo</option>
                  <option value={0}>Inativo</option>
                </select>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-muted px-6 py-3 rounded-b-lg sm:flex sm:flex-row-reverse gap-2">
              <button
                type="submit"
                disabled={submitDisabled}
                className="inline-flex w-full justify-center rounded-lg border border-transparent bg-purple-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="bi bi-hourglass-split animate-spin mr-2" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg mr-2" />
                    Salvar
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="mt-3 inline-flex w-full justify-center rounded-lg border border-border bg-card px-4 py-2 text-base font-medium text-foreground shadow-sm hover:bg-muted focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>

    </div>
  );
}
